<template>
    <transition
        enter-active-class="ease-out duration-300"
        enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enter-to-class="opacity-100 translate-y-0 sm:scale-100"
        leave-active-class="ease-in duration-200"
        leave-class="opacity-100 translate-y-0 sm:scale-100"
        leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
        <div v-show="show" class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
            <form method="POST" @submit.prevent="retry">
                <div class="mb-4">
                    <div :class="`mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-${$root.theme}-100`">
                            <svg :class="`h-8 w-8 text-${$root.theme}-600`" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <div class="mt-4 text-center">
                        <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                            Retry Payment
                        </h3>
                        <div class="mt-4 text-left">
                            <p class="text-gray-500 mb-1 px-6 text-center">
                                Are you sure you want to re-try this payment for:
                            </p>
                            <p v-if="show" class="text-center text-xl mb-4 font-semibold">{{ payment.amount | money }}</p>

                            <select v-model="form.payment.type"
                                :id="`${unique}_payment_method`"
                                name="payment_method"
                                :class="`bg-gray-50 focus:ring-${$root.theme}-400 focus:border-${$root.theme}-400 mt-1 block w-full pl-4 pr-10 py-3 text-base border-gray-300 focus:outline-none sm:text-sm rounded-md`">
                                <option :value="null">Choose Method:</option>
                                <option value="same" v-if="billingProfile && billingProfile.payment_type">
                                    {{ billingProfile.payment_type === 'check' ? 'Checking' : billingProfile.cc_type }} ••••
                                    {{ billingProfile.account_last_4 }}
                                </option>
                                <option v-if="settings.enable_ach" value="check">Pay with Personal Bank account</option>
                                <option value="card">Pay with Credit/Debit Card</option>
                            </select>

                            <pay-with-bank v-if="form.payment.type === 'check'"
                                :first_name.sync="form.payment.first_name"
                                :last_name.sync="form.payment.last_name"
                                :routing_number.sync="form.payment.routing_number"
                                :account_number.sync="form.payment.account_number"
                                :error="error" class="mt-4" />

                            <pay-with-card v-else-if="form.payment.type === 'card'"
                                :settings="settings"
                                :first_name.sync="form.payment.first_name"
                                :last_name.sync="form.payment.last_name"
                                :address.sync="form.payment.address"
                                :city.sync="form.payment.city"
                                :state.sync="form.payment.state"
                                :cc_number.sync="form.payment.cc_number"
                                :card_exp.sync="form.payment.card_exp"
                                :cvv.sync="form.payment.cvv"
                                :zip.sync="form.payment.zip"
                                :error="error" class="mt-4" />

                            <p class="mt-2 px-8 text-sm text-gray-600 text-center">Choose current payment or create a new one above.</p>

                        </div>
                    </div>
                </div>
                <p v-if="error" class="mb-2 text-sm text-red-600" id="email-error">Card declined.</p>
                <div class="sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                    <button type="submit" :class="`w-full inline-flex text-black justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-${$root.theme}-300 text-base font-medium hover:bg-${$root.theme}-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${$root.theme}-500 sm:col-start-2 sm:text-sm`">
                        Re-try Payment
                    </button>
                    <button @click="nevermind" type="button" :class="`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${$root.theme}-500 sm:mt-0 sm:col-start-1 sm:text-sm`">
                        Nevermind
                    </button>
                </div>
                <div v-if="remainingDeclined" class="mt-4 flex justify-center items-center px-6 space-x-4">
                    <input type="checkbox" id="multiple" name="multiple" :class="`rounded-md border border-gray-300 shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${$root.theme}-500`" />
                    <label for="multiple" class="text-sm text-gray-600">Retry remaining{{ remainingDeclined > 1 ? " " + remainingDeclined : ' ' }} declined payment{{ remainingDeclined > 1 ? 's' : '' }}.</label>
                </div>
                <p class="mt-4 text-sm leading-tight font-medium text-gray-400">
                    <template v-if="$parent.consumerPortalNumber !== '' && $parent.consumerPortalEmail !== ''">
                        In order to change your arrangements (dates and / or amounts) please contact our office at <a :href="`tel:${$parent.consumerPortalNumber}`" :class="`font-semibold text-${$root.theme}-500`">{{ $parent.consumerPortalNumber }}</a> or email us at  <a :href="`mailto:${$parent.consumerPortalEmail}`" :class="`font-semibold text-${$root.theme}-500`">{{ $parent.consumerPortalEmail }}.</a>
                    </template>
                    <template v-else-if="$parent.consumerPortalNumber !== '' && ! $parent.consumerPortalEmail !== ''">
                        In order to change your arrangements (dates and / or amounts) please contact our office at <a :href="`tel:${$parent.consumerPortalNumber}`" :class="`font-semibold text-${$root.theme}-500`">{{ $parent.consumerPortalNumber }}.</a>
                    </template>
                    <template v-else-if="$parent.consumerPortalEmail !== '' && ! $parent.consumerPortalNumber !== ''">
                        In order to change your arrangements (dates and / or amounts) please email our office at <a :href="`mailto:${$parent.consumerPortalEmail}`" :class="`font-semibold text-${$root.theme}-500`">{{ $parent.consumerPortalEmail }}.</a>
                    </template>
                </p>
            </form>
        </div>
    </transition>
</template>

<script>
    import Mixins from '../../mixins';
    import PayWithCard from '../PaymentMethods/PayWithCard';
    import PayWithBank from '../PaymentMethods/PayWithBank';

    const basePayment = {
        payment_id: '',
        type: 'check',
        amount: null,
        payment_date: '',
        frequency: 'monthly',
        email: '',
        first_name: '',
        last_name: '',
        address: '',
        city: '',
        state: '',

        routing_number: '',
        account_number: '',

        cc_number: '',
        card_exp: '',
        exp_month: '',
        exp_year: '',
        cvv: '',
        zip: ''
    };

    export default {
        mixins: [Mixins],

        components: {
            PayWithBank,
            PayWithCard
        },

        props: ['show', 'account', 'payments', 'billing-profile', 'settings'],

        created() {
            this.unique = Math.floor(Math.random() * 100);
        },

        /**
         * Auto set today as payment_date and if ACH is disabled
         * in settings, then change method to card.
         *
         * @returns {void}
         */
        watch: {
            show: {
                immediate: true,
                handler: function (show, oldShow) {
                    if (show && ! oldShow) {
                        this.form.payment.payment_id = this.payment.id;
                    } else if (! show && oldShow) {
                        this.form.payment = {...basePayment};
                    } else if (! show && ! oldShow) {
                        this.form.payment = {...basePayment};
                    }

                    if (! this.settings.enable_ach && ! this.billingProfile) {
                        this.form.payment.type = 'card';
                    } else if (this.billingProfile) {
                        this.form.payment.type = 'same';
                    }
                }
            }
        },

        data() {
            return {
                unique: null,
                error: null,
                loading: false,
                form: {
                    payment: null
                }
            }
        },

        computed: {
            /**
             * @returns {*}
             */
            payment() {
                return this.payments[0];
            },

            remainingDeclined() {
                if (this.payments) return this.payments.length - 1;

                return null;
            }
        },

        methods: {
            /**
             * @return {void|null}
             */
            retry() {
                this.error = false;

                if (this.loading) return;

                this.loading = true;

                this.submitPayment({
                    submittedPaymentMethod: this.form.payment,
                    apiUrl: `${this.$parent.consumerPortalBaseUrl}/api/retry-payments`,
                    afterCallback: () => {
                        this.gtagEvent('offer-accepted:NSF-Retry');

                        setTimeout(() => {
                            window.location.reload();
                        }, 150);

                        this.$emit('close');

                        this.loading = false;
                    },
                    onError: error => {
                        if (error.hasOwnProperty('body')) {
                            this.error = error.body.error;
                        } else {
                            throw error;
                        }

                        this.loading = false;
                    },
                    withLoading: false
                });
            },

            /**
             * @return {void}
             */
            nevermind() {
                this.$emit('close');
            },
        }
    }
</script>
